import { Injectable } from '@angular/core';

/**
 * DeviceIdService
 * Generates and persists a unique device ID for this browser/device
 * Used for NATS notification targeting and device registration
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceIdService {
  private readonly STORAGE_KEY = 'cipher_device_id';
  private readonly STORAGE_KEY_CREATED = 'cipher_device_id_created';
  private deviceId: string | null = null;

  constructor() {
    this.deviceId = this.loadOrGenerate();
  }

  /**
   * Get the current device ID
   */
  getDeviceId(): string {
    if (!this.deviceId) {
      this.deviceId = this.loadOrGenerate();
    }
    return this.deviceId;
  }

  /**
   * Get device ID creation timestamp
   */
  getCreatedAt(): Date | null {
    const stored = localStorage.getItem(this.STORAGE_KEY_CREATED);
    return stored ? new Date(stored) : null;
  }

  /**
   * Force regeneration of device ID (use with caution)
   */
  regenerate(): string {
    this.deviceId = this.generateDeviceId();
    this.saveToStorage(this.deviceId);
    return this.deviceId;
  }

  /**
   * Clear device ID (e.g., on logout if needed)
   */
  clear(): void {
    this.deviceId = null;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_KEY_CREATED);
    this.clearIndexedDB();
  }

  private loadOrGenerate(): string {
    // Try localStorage first
    let id = localStorage.getItem(this.STORAGE_KEY);
    if (id && this.isValidDeviceId(id)) {
      return id;
    }

    // Try IndexedDB fallback
    id = this.loadFromIndexedDB();
    if (id && this.isValidDeviceId(id)) {
      // Sync back to localStorage
      localStorage.setItem(this.STORAGE_KEY, id);
      return id;
    }

    // Generate new ID
    id = this.generateDeviceId();
    this.saveToStorage(id);
    return id;
  }

  private generateDeviceId(): string {
    // Generate a UUID v4-like ID with browser fingerprint components
    const timestamp = Date.now().toString(36);
    const randomPart = this.generateRandomString(16);
    const fingerprint = this.getBrowserFingerprint();
    
    return `web_${timestamp}_${randomPart}_${fingerprint}`.substring(0, 64);
  }

  private generateRandomString(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
    return result;
  }

  private getBrowserFingerprint(): string {
    // Simple browser fingerprint based on available properties
    const components: string[] = [];
    
    try {
      components.push(navigator.userAgent.length.toString());
      components.push(navigator.language || '');
      components.push(screen.width.toString());
      components.push(screen.height.toString());
      components.push(new Date().getTimezoneOffset().toString());
      components.push(navigator.hardwareConcurrency?.toString() || '0');
    } catch {
      components.push('fallback');
    }

    // Create a simple hash
    const str = components.join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  }

  private isValidDeviceId(id: string): boolean {
    return typeof id === 'string' && id.length >= 10 && id.startsWith('web_');
  }

  private saveToStorage(id: string): void {
    const now = new Date().toISOString();
    localStorage.setItem(this.STORAGE_KEY, id);
    localStorage.setItem(this.STORAGE_KEY_CREATED, now);
    this.saveToIndexedDB(id, now);
  }

  // IndexedDB fallback for when localStorage is cleared
  private saveToIndexedDB(id: string, createdAt: string): void {
    try {
      const request = indexedDB.open('CipherDeviceDB', 1);
      
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('device')) {
          db.createObjectStore('device', { keyPath: 'key' });
        }
      };

      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const tx = db.transaction('device', 'readwrite');
        const store = tx.objectStore('device');
        store.put({ key: 'deviceId', value: id, createdAt });
      };
    } catch {
      // IndexedDB not available, localStorage only
    }
  }

  private loadFromIndexedDB(): string | null {
    // Note: This is synchronous fallback - actual IndexedDB is async
    // For proper async handling, this would need to be refactored
    // For now, we primarily rely on localStorage with IndexedDB as backup
    return null;
  }

  private clearIndexedDB(): void {
    try {
      const request = indexedDB.open('CipherDeviceDB', 1);
      request.onsuccess = (event: any) => {
        const db = event.target.result;
        if (db.objectStoreNames.contains('device')) {
          const tx = db.transaction('device', 'readwrite');
          const store = tx.objectStore('device');
          store.delete('deviceId');
        }
      };
    } catch {
      // Ignore errors
    }
  }
}
